import {groupBy, groupByUniqueKey, mapObject} from '@benchling/js-utils';
import {flushToStyleTag} from 'aphrodite';
import React, {useLayoutEffect, useState, useMemo, useCallback, useRef} from 'react';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import {makeReactContext} from 'lib/makeReactContext';
import {
  CanvasGridCoordinates,
  MethodNode,
  ProcedureFlowchart,
  ProcedureEdgeType,
  ProcedureEdge,
} from 'procedures/ProcedureDomainTypes';
import {useDebounce} from 'ui-library/hooks/useDebounce';

import {getNextLinearMethodNode} from './flowchartManagementUtils';
import {createEdgeId} from './handleIdentifierUtils';
import {nodeZIndexStyle} from './procedureMethodNodeCommonConsts';
import {
  ConvertedProcedureGraph,
  MethodEdgeCallbacks,
  MethodNodeCallbacks,
  MaterialEdgeCallbacks,
  MethodCanvasNode,
  ProcedureCanvasEdge,
} from './ProcedureReactFlowCanvasTypes';

interface XYPosition {
  x: number;
  y: number;
}

export const STUBBABLE_UTILS = {
  getDOMElementDimensions(element: HTMLElement | null): NodeDimensions {
    // Dummy value so react can do the first render of new nodes
    if (element == null) {
      return {width: 0, height: 0};
    }
    const {width, height} = element.getBoundingClientRect();
    // Don't pass raw DOMRect objects so deep equality checks can work properly
    return {width, height};
  },
};

export const COLUMN_GAP_PIXELS = 100;
export const ROW_GAP_PIXELS = 40;

interface GridLayoutContext {
  nodeCidToDOMElement: Record<string, HTMLElement | null>;
}
const GridLayoutContext = makeReactContext<GridLayoutContext>(
  () => {
    assert(false, 'Grid layout engine requires GridLayoutContext to be set up');
  },
  'GridLayoutContext',
  () => true // Ref-like behavior: this is not a reactive value, it should never trigger re-renders.
);

export const GridLayoutContextProvider = (props: React.PropsWithChildren<{}>): JSX.Element => {
  const contextObject = useRef<GridLayoutContext>({
    nodeCidToDOMElement: {},
  });

  return (
    <GridLayoutContext.Provider value={contextObject.current}>{props.children}</GridLayoutContext.Provider>
  );
};

/**
 * Every canvas node component must construct a RefCallback using the returned function here, and install
 * it into the outermost wrapper <div> in the component.
 *
 * This is necessary to enable the grid layout engine to perform runtime inspections of canvas node element
 * dimensions and calculate grid track sizes!
 */
export function useGridLayoutNodeDOMRefCallback(): (methodCid: string, domNode: HTMLElement | null) => void {
  const {nodeCidToDOMElement} = GridLayoutContext.useContext();
  const gridRefCallback = useCallback(
    (methodCid, domNode) => {
      nodeCidToDOMElement[methodCid] = domNode;
    },
    [nodeCidToDOMElement]
  );
  return gridRefCallback;
}

function assertNodesHaveUniqueGridCoordinates(nodes: Array<MethodNode>): void {
  const coordinatesSet = new Set(
    nodes.map(
      ({canvasGridCoordinates}) => `row${canvasGridCoordinates.row}-col${canvasGridCoordinates.column}`
    )
  );
  assert(coordinatesSet.size === nodes.length, 'No two nodes can have the same grid coordinates');
}

interface NodeDimensions {
  width: number;
  height: number;
}

// Node attributes that can affect the grid layout
interface GridLayoutNodeDependencies {
  dimensions: NodeDimensions;
  canvasGridCoordinates: CanvasGridCoordinates;
}

/** For a given dimension (x or y), compute the dimensional coordinate every node should be placed at
 * (according to its canvasGridCoordinates) to produce the desired grid layout. The returned values are in the
 * canvas (eg. react flow) coordinate space.
 *
 * This function accounts for nodes with different lengths (width or height) and ensures every node has at
 * least `coordinateOptions.pixelGap` space between it and its adjacent nodes. Grid rows or columns that have
 * no nodes in them will be collapsed - there will be no empty columns or rows.
 *
 * @param nodes Flowchart nodes
 * @param coordinateOptions Parameterized args to differentiate between X and Y dimensions for accessors/transformers
 * @returns Mapping of node id -> one-dimentional canvas position
 */
function getCanvasCoordinateForSingleAxis(
  nodes: Array<MethodNode>,
  coordinateOptions: {
    canvasCoordinateToClientCoordinate: (rfCoord: number) => number;
    clientCoordinateToCanvasCoordinate: (clientCoord: number) => number;
    getSingleAxisGridCoordinate: (node: MethodNode) => number;
    getSingleAxisSize: (nodeCid: string) => number;
    canvasOriginCoordinate: number;
    pixelGap: number;
  }
): {
  nodeCidToComputedCanvasPos: {[nodeCid: string]: number};
  maxClientLengthByGridCoordinate: {[col: number]: number};
} {
  const nodesByGridCoordinate = groupBy(nodes, coordinateOptions.getSingleAxisGridCoordinate);
  // This is how wide each row/col will be
  const maxClientLengthByGridCoordinate: {[col: number]: number} = mapObject(nodesByGridCoordinate, (nodes) =>
    Math.max(...nodes.map(({cid}) => coordinateOptions.getSingleAxisSize(cid)))
  );
  const sortedGridCoordinateValues = Object.keys(maxClientLengthByGridCoordinate)
    .map((coordinateString) => Number(coordinateString))
    .sort((a, b) => a - b);

  // Previous iteration passes us the correct x/y position for all nodes in the current coordinate
  let canvasPos = coordinateOptions.canvasOriginCoordinate;
  const nodeCidToComputedCanvasPos: {[nodeCid: string]: number} = {};
  // Efficiently compute each node's x/y values by iterating in grid coordinate order and building on the
  // previous node's position.
  for (const gridCoordinate of sortedGridCoordinateValues) {
    for (const node of nodesByGridCoordinate[gridCoordinate]) {
      nodeCidToComputedCanvasPos[node.cid] = canvasPos;
    }
    // Calculate the next coordinate's position
    // Dip into client coordinate space because our lengths are in client pixels
    const nodeEndClientPos =
      coordinateOptions.canvasCoordinateToClientCoordinate(canvasPos) +
      maxClientLengthByGridCoordinate[gridCoordinate];
    // Back to react-flow coordinate space
    const nodeEndCanvasPos = coordinateOptions.clientCoordinateToCanvasCoordinate(nodeEndClientPos);
    canvasPos = nodeEndCanvasPos + coordinateOptions.pixelGap;
  }
  return {nodeCidToComputedCanvasPos, maxClientLengthByGridCoordinate};
}

function buildMethodCanvasNode(
  method: MethodNode,
  methodNodeCallbacks: MethodNodeCallbacks,
  nodeCidToComputedCanvasPos: {[nodeCid: string]: XYPosition}
): MethodCanvasNode {
  return {
    id: method.cid,
    type: method.type,
    position: nodeCidToComputedCanvasPos[method.cid] ?? {x: 0, y: 0}, // Position anywhere for first render so we can extract the sizes and compute the proper placement
    data: {...method, ...methodNodeCallbacks},
    style: {
      ...nodeZIndexStyle,
    },
  };
}

function buildProcedureCanvasEdge(
  edge: ProcedureEdge,
  flowchart: ProcedureFlowchart,
  methodNodes: Array<MethodNode>,
  methodEdgeCallbacks: MethodEdgeCallbacks,
  materialEdgeCallbacks: MaterialEdgeCallbacks
): ProcedureCanvasEdge {
  const {sourceHandle, targetHandle} = edge;
  const baseCanvasEdge: Omit<ProcedureCanvasEdge, 'data' | 'type'> = {
    id: createEdgeId(sourceHandle, targetHandle),
    source: edge.fromMethodCid,
    target: edge.toMethodCid,
    sourceHandle,
    targetHandle,
  };
  let canvasEdge: ProcedureCanvasEdge;
  switch (edge.type) {
    case ProcedureEdgeType.METHOD_EDGE: {
      canvasEdge = {
        ...baseCanvasEdge,
        type: edge.type,
        data: {...edge, ...methodEdgeCallbacks},
      };
      break;
    }
    case ProcedureEdgeType.MATERIAL_EDGE: {
      const nextMethod = getNextLinearMethodNode(flowchart, edge.fromMethodCid);
      const doesMaterialEdgeHaveMatchingMethodEdge = nextMethod?.cid === edge.toMethodCid;
      canvasEdge = {
        ...baseCanvasEdge,
        type: edge.type,
        data: {
          ...edge,
          ...materialEdgeCallbacks,
          allMethodNodesInFlowchart: methodNodes,
          doesMaterialEdgeHaveMatchingMethodEdge,
        },
      };
      break;
    }
    default:
      assert(false, 'unsupported edge type');
  }
  return canvasEdge;
}

// Transform graph for react flow ingestion (apply layout, compute react flow options, etc)
function buildConvertedProcedureGraph(
  flowchart: ProcedureFlowchart,
  nodeCidToComputedCanvasPos: {[nodeCid: string]: XYPosition},
  methodNodeCallbacks: MethodNodeCallbacks,
  methodEdgeCallbacks: MethodEdgeCallbacks,
  materialEdgeCallbacks: MaterialEdgeCallbacks,
  debouncedMethodNodes: Array<MethodNode>
): ConvertedProcedureGraph {
  return {
    nodes: flowchart.nodes.map((node) =>
      buildMethodCanvasNode(node, methodNodeCallbacks, nodeCidToComputedCanvasPos)
    ),
    edges: flowchart.edges.map((edge) =>
      buildProcedureCanvasEdge(
        edge,
        flowchart,
        debouncedMethodNodes,
        methodEdgeCallbacks,
        materialEdgeCallbacks
      )
    ),
  };
}

/**
 * You MUST make sure that GridLayoutContext is set up to be accessible from the caller of this hook and the
 * the graph node renderer.
 * You MUST make sure that the graph node renderer component installs useGridLayoutNodeDOMRefCallback's
 * callback ref into its root <div>.
 */
export function useConvertedGridLayoutGraph(
  procedureFlowchart: ProcedureFlowchart,
  methodNodeCallbacks: MethodNodeCallbacks,
  methodEdgeCallbacks: MethodEdgeCallbacks,
  materialEdgeCallbacks: MaterialEdgeCallbacks,
  canvasSpaceOptions: {
    clientPointToCanvasPoint: (pos: XYPosition) => XYPosition;
    canvasPointToClientPoint: (pos: XYPosition) => XYPosition;
    clientOriginPos: XYPosition;
  }
): {
  convertedProcedureGraph: ConvertedProcedureGraph;
  nodeCidToComputedCanvasPos: {[nodeCid: string]: XYPosition};
  maxHeightByRow: {[row: number]: number};
} {
  assertNodesHaveUniqueGridCoordinates(procedureFlowchart.nodes);

  const {clientOriginPos, clientPointToCanvasPoint, canvasPointToClientPoint} = canvasSpaceOptions;
  const {nodeCidToDOMElement} = GridLayoutContext.useContext();
  const [cachedNodeCidToLayoutDependencies, setCachedNodeCidToLayoutDependencies] = useState<{
    [nodeCid: string]: GridLayoutNodeDependencies;
  }>({});
  const [nodeCidToComputedCanvasPos, setNodeCidToComputedCanvasPos] = useState<{
    [nodeCid: string]: XYPosition;
  }>({});
  const [cachedMaxHeightByRow, setCachedMaxHeightByRow] = useState<{[col: number]: number}>({});
  const debouncedMethodNodes = useDebounce(procedureFlowchart.nodes, 0);

  // Computes and updates `nodeCidToComputedCanvasPos` to produce the desired grid layout
  useLayoutEffect(() => {
    const nodeByCid = groupByUniqueKey(procedureFlowchart.nodes, ({cid}) => cid);
    const nodeCidToLayoutDependencies = mapObject(nodeByCid, ({cid, canvasGridCoordinates}) => ({
      dimensions: STUBBABLE_UTILS.getDOMElementDimensions(nodeCidToDOMElement[cid]),
      canvasGridCoordinates,
    }));

    // Avoid infinite useLayoutEffect <-> setState loop and minimize unnecessary ops
    if (isEqual(cachedNodeCidToLayoutDependencies, nodeCidToLayoutDependencies)) {
      return;
    }
    flushToStyleTag();
    const canvasOriginPos = clientPointToCanvasPoint(clientOriginPos);
    // X coordinates for each node to achieve grid layout. Can be calculated in isolation from Y coords.
    const {nodeCidToComputedCanvasPos: nodeCidToCanvasX} = getCanvasCoordinateForSingleAxis(
      procedureFlowchart.nodes,
      {
        canvasCoordinateToClientCoordinate: (x) => canvasPointToClientPoint({x, y: 0}).x,
        clientCoordinateToCanvasCoordinate: (x) => clientPointToCanvasPoint({x, y: 0}).x,
        getSingleAxisGridCoordinate: (node) => node.canvasGridCoordinates.column,
        getSingleAxisSize: (nodeCid) => nodeCidToLayoutDependencies[nodeCid].dimensions.width,
        pixelGap: COLUMN_GAP_PIXELS,
        canvasOriginCoordinate: canvasOriginPos.x,
      }
    );
    // Y coordinates for each node to achieve grid layout. Can be calculated in isolation from X coords.
    const {nodeCidToComputedCanvasPos: nodeCidToCanvasY, maxClientLengthByGridCoordinate: maxHeightByRow} =
      getCanvasCoordinateForSingleAxis(procedureFlowchart.nodes, {
        canvasCoordinateToClientCoordinate: (y) => canvasPointToClientPoint({x: 0, y}).y,
        clientCoordinateToCanvasCoordinate: (y) => clientPointToCanvasPoint({x: 0, y}).y,
        getSingleAxisGridCoordinate: (node) => node.canvasGridCoordinates.row,
        getSingleAxisSize: (nodeCid) => nodeCidToLayoutDependencies[nodeCid].dimensions.height,
        pixelGap: ROW_GAP_PIXELS,
        canvasOriginCoordinate: canvasOriginPos.y,
      });

    const computedNodeCidToCanvasPos = mapObject(nodeByCid, ({cid}) => ({
      x: nodeCidToCanvasX[cid],
      y: nodeCidToCanvasY[cid],
    }));

    setNodeCidToComputedCanvasPos(computedNodeCidToCanvasPos);
    setCachedNodeCidToLayoutDependencies(nodeCidToLayoutDependencies);
    setCachedMaxHeightByRow(maxHeightByRow);
  }, [
    procedureFlowchart,
    nodeCidToComputedCanvasPos,
    cachedNodeCidToLayoutDependencies,
    nodeCidToDOMElement,
    clientOriginPos,
    clientPointToCanvasPoint,
    canvasPointToClientPoint,
  ]);

  const convertedProcedureGraph = useMemo(
    () =>
      buildConvertedProcedureGraph(
        procedureFlowchart,
        nodeCidToComputedCanvasPos,
        methodNodeCallbacks,
        methodEdgeCallbacks,
        materialEdgeCallbacks,
        debouncedMethodNodes
      ),
    [
      procedureFlowchart,
      nodeCidToComputedCanvasPos,
      methodNodeCallbacks,
      methodEdgeCallbacks,
      materialEdgeCallbacks,
      debouncedMethodNodes,
    ]
  );

  return {convertedProcedureGraph, nodeCidToComputedCanvasPos, maxHeightByRow: cachedMaxHeightByRow};
}
